import React from "react";
import {normalizeColor} from "grommet/utils";
import StatusIcon from "../components/StatusIcon";
import {Box} from "grommet";
import {
    IconX as IconClose
} from '@tabler/icons-react';
import Icon from "../components/Icon";
// Every drop's edge (global.drop below). Options and suggestions inside a drop round their
// corners to its inner curve, and anything sized to the screen leaves room for the border.
const DROP_RADIUS = '4px';
const DROP_BORDER = '1.5px';
const DROP_INNER_RADIUS = `calc(${DROP_RADIUS} - ${DROP_BORDER})`;
// The screen less the 12px gutter each side and the drop's border
export const DROP_MAX_WIDTH = `calc(100vw - 24px - 2 * ${DROP_BORDER})`;

const theme = {
    name: 'Streamlined',
    rounding: 4,
    spacing: 24,
    defaultMode: 'light',
    global: {
        colors: {
            // Brand colors ========================================
            cinder: {
                light: '#242729',
                dark: '#181A1B'
            },
            invisible: 'transparent',
            tan: {
                light: '#FAF3ED',
                dark: '#FAF3ED'
            },
            lemon: {
                light: '#FFCF49',
                dark: '#D4AF47'
            },
            lemonade: {
                light: '#FABF1B',
                dark: '#FABF1B',
            },
            'summer-sky': {
                light: '#75D8F5',
                dark: '#6DBDD4'
            },
            'light-sky-blue': '#A3D8F4',
            magento: {
                light: '#9A69FF',
                dark: '#9A69FF'
            },
            'alice-blue': {
                light: '#D2EAFB',
                dark: '#5A5E60'
            },
            'smoke': {
                light: '#F4F4F4',
                dark: '#1F2122'
            },
            'black': '#000000',
            'billowing-clouds-weak': {
                light: '#D8DDE388',
                dark: '#AAAAAA44'
            },
            'billowing-clouds': {
                light: '#D8DDE3',
                dark: '#666666'
            },
            trout: {
                light: '#4E5153',
                dark: '#4E5153'
            },
            'alice-blue-light': {
                light: '#E9F5FD',
                dark: '#4E5153'
            },
            'alice-blue-xlight': {
                light: '#F9FCFE',
                dark: '#4E5153'
            },
            "green-weak": {
                light: '#48DC5B88',
                dark: '#48DC5BCC',
            },
            "green-strong": {
                light: '#23B535',
                dark: '#88E895'
            },
            // Good news as small text: 4.5:1 on the background, which the chart greens aren't
            "text-good": {
                light: '#13852A',
                dark: '#88E895'
            },
            // Partner colors,
            "shopify-green": {
                light: '#96BF48',
                dark: '#96BF48'
            },
            "quickbooks-green": {
                light: '#2B9F1B',
                dark: '#2B9F1B'
            },
            white: {
                light: '#FFFFFF',
                dark: '#FFFFFF'
            },
            // Status colors ========================================
            'status-ok': {
                light: '#48DC5B',
                dark: '#48DC5BCC'
            },
            'status-warning-1': {
                light: '#FFCA58',
                dark: '#FFCA58'
            },
            'status-warning-2': {
                light: '#FFAA15',
                dark: '#FFAA15'
            },
            'status-warning': 'status-warning-2',
            'status-critical-1': {
                light: '#F96401',
                dark: '#F96401'
            },
            'status-critical-2': {
                light: '#E60000',
                dark: '#FF6666'
            },
            'status-critical': 'status-critical-2',
            'status-info': {
                light: '#95D1FF',
                dark: '#95D1FF' // TODO look at this
            },
            'status-info-heavy': {
                light: '#59B8FF',
                dark: '#59B8FF' // TODO look at this
            },
            'status-disabled': {
                light: '#EEEEEE',
                dark: '#AAAAAA'
            },
            'neutral': {
                light: '#000000',
                dark: '#FFFFFF'
            },
            'neutral-1': {
                light: '#222222',
                dark: '#EFEFEF'
            },
            'neutral-2': {
                light: '#444444',
                dark: '#E9E9E9'
            },
            'neutral-3': {
                light: '#666666',
                dark: '#CCCCCC'
            },
            'neutral-4': {
                light: '#888888',
                dark: '#AAAAAA'
            },
            'neutral-5': {
                light: '#AAAAAA',
                dark: '#888888'
            },
            'neutral-6': {
                light: '#CCCCCC',
                dark: '#666666'
            },
            'neutral-7': {
                light: '#E9E9E9',
                dark: '#444444'
            },
            'neutral-8': {
                light: '#EFEFEF',
                dark: '#222222'
            },
            'neutral-9': {
                light: '#FFFFFF',
                dark: '#000000'
            },
            //========= Badge colors
            'invoice-has-credit': {
                light: '#95E24A',
                dark: '#48DC5B66',
            },
            'invoice-closed': {
                light: '#C7EEA0',
                dark: '#48DC5B44',
            },
            'invoice-processing': {
                light: 'accent',
                dark: 'accent',
            },
            'invoice-not-yet-due': {
                light: 'accent',
                dark: 'accent'
            },
            'invoice-overdue': {
                light: '#FDF5E4',
                dark: '#FFCA5866',
            },
            'invoice-overdue-30+': {
                light: '#FDEDC4',
                dark: '#FFAA1566',
            },
            'invoice-overdue-60+': {
                light: '#FFDCB2',
                dark: '#F9640166',
            },
            'invoice-overdue-90+': {
                light: '#FFCEAE',
                dark: '#FF000066',
            },
            'invoice-voided': {
                light: '#E7E7E7',
                dark: '#E7E7E788',
            },
            'type-single-invoice':{
                light: '#FDEDC4',
                dark: '#FFAA1566',
            },
            'type-recurring-invoice': {
                light: '#95E24A66',
                dark: '#48DC5B66',
            },
            'type-milestone-invoice': 'alice-blue',
            //======== Chart colors
            "barchart-primary": {
                light: "#159F2B",
                dark: "#159F2B"
            },
            "barchart-secondary": {
                light: "#D6ECBF",
                dark: "#D6ECBF"
            },
            // How a customer pays. Each sits next to its name and share, so color is never alone
            "chart-method-ach": { light: "#159F2B", dark: "#159F2B" },
            "chart-method-check": { light: "#2F7FD6", dark: "#4C8FD8" },
            "chart-method-card": { light: "#B4569E", dark: "#C26BAE" },
            "chart-method-wire": { light: "#B87A0F", dark: "#B07F18" },
            "chart-method-other": { light: "#8E97A1", dark: "#77797D" },
            // The quiet series beside barchart-primary, and placeholder marks with no data
            "chart-muted": {
                light: "#DDE1E6",
                dark: "#55575B"
            },
            "piechart-secondary": {
                light: "#EAEFFB",
                dark: "accent"
            },
            //========= Email colors
            // The React Email package's own palette, for previewing an email that renders from
            // it (backend/emails/src/lib/theme.ts). Light value in both app themes: those
            // emails send `color-scheme: light`, so a preview that followed the app into dark
            // mode would be showing the merchant something no recipient sees.
            //
            // The tinted banner colours that used to sit here went with the SendGrid previews
            // they were mixed for.
            "email-canvas": {
                light: "#F3F4F6",
                dark: "#F3F4F6"
            },
            "email-card": {
                light: "#FFFFFF",
                dark: "#FFFFFF"
            },
            "email-card-border": {
                light: "#EAEDF0",
                dark: "#EAEDF0"
            },
            "email-text": {
                light: "#1A1D21",
                dark: "#1A1D21"
            },
            // theme.muted, between text and subtle: the colour the emails give a line that
            // supports the one above it, such as an invoice's due date under its amount.
            "email-text-muted": {
                light: "#5B6573",
                dark: "#5B6573"
            },
            "email-text-subtle": {
                light: "#8A94A3",
                dark: "#8A94A3"
            },
            //========= Color definition here
            background: {
                light: '#FFFFFF',
                dark: '#181A1B'
            },
            foreground: {
                light: '#FFFFFF',
                dark: "#292A2C"
            },
            "primary-side-bar": {
                light: '#F9FCFE', // alice blue light,
                dark: "#292A2C" // foreground
            },
            brand: 'lemon',
            primary: 'lemon',
            secondary: 'summer-sky',
            "border-weak": 'billowing-clouds-weak',
            border: 'billowing-clouds',
            "border-strong": "neutral-6",
            "border-xstrong": "neutral-4",
            accent: 'alice-blue',
            cta: 'lemon',
            'cta-hover': 'lemonade',
            control: 'lemon',
            text: 'neutral',
            'text-weak': {
                light: 'neutral-4',
                dark: 'neutral-3'
            },
            'text-xweak': {
                light: 'neutral-5',
                dark: 'neutral-5'
            },
            'text-reverse': 'neutral-9',
            'text-highlight': {
                light: '#FFFF8F',
                dark: '#73733E'
            },
            'text-info': {
                light: '#86bce5',
                dark: '#a7cbe6'
            },
            focus: {
                light: 'magento',
                dark: '#9487E8'
            },
            active: {
                light: 'alice-blue',
                dark: 'alice-blue-xlight'
            },
            hover: {
                light: '#E3F1FC',
                dark: '#FFFFFF22'
            },
            // Hover for everything that uses global.hover (hoverIndicator buttons, List rows,
            // Menu items, Select options, accordion headers): a lighter tint than table rows
            // ('hover') in light mode, by choice; the same as them in dark mode
            'hover-subtle': {
                light: '#EEF7FD',
                dark: '#FFFFFF22'
            },
            // Secondary button hover. Solid in dark mode: a hover replaces the button's own
            // (opaque) background, so a translucent colour showed the page through and looked
            // the same as the button. This is #FFFFFF22 over the dark foreground.
            'hover-secondary': {
                light: '#EEF7FD',
                dark: '#45464A'
            },
        },
        selected: {
            background: 'focus'
        },
        hoverIndicator: "hover",
        font: {
            family: 'Calibre, sans-serif',
            size: '18px',
            height: '24px',
            maxWidth: '432px',
        },
        borderSize: {
            xsmall: '1.5px',
            small: '2px',
            medium: '4px',
            large: '12px',
            xlarge: '24px'
        },
        input: {
            weight: 400,
            extend: ({size}) => {
                let paddingSizes = {
                    small: '4px 8px',
                    medium: '12px',
                    large: '12px',
                    xlarge: '12px'
                }
                return `padding: ${paddingSizes[size]};`
            }
        },
        control: {
            border: {
                color: 'border-strong',
                width: '1.5px',
            }
        },
        spacing: '24px',
        focus: {
            shadow: {
                color: '#00000000'
            },
            border: {
                color: 'focus'
            }
        },
        breakpoints: {
            small: {
                value: 768
            },
            medium: {
                value: 1408
            }
        },
        hover: {
            // A plain colour name, not { color }: Grommet's base theme merges opacity
            // 'medium' (40%) into an object here, which left hovers near-invisible in dark
            // mode. A string replaces the base object, so the colour is used as-is.
            // Grommet reads this one value for hoverIndicator buttons, List rows, Menu items
            // and Select options alike, so all of them use hover-subtle; only table rows
            // (components/DataTable.js) keep the stronger 'hover'.
            background: 'hover-subtle',
        },
        drop: {
            background: 'foreground',
            shadowSize: 'none',
            elevation: 'small',
            border: {
                radius: DROP_RADIUS,
                size: "small"
            },
            margin: {
                vertical: 'xsmall'
            },
            // The small shadow is too faint to see in light mode, so the border is what gives
            // a drop its edge. Every drop gets it here rather than each caller remembering a
            // border, which half of them forgot. Tips render through Drop too, around their
            // own bordered content, so their drop is plain (tip.drop below) and left alone.
            extend: ({ theme, plain }) => {
                return plain ? '' : `border: ${ DROP_BORDER } solid ${ normalizeColor(theme.global.colors['border'], theme) };`;
            }
        },
        elevation: {
            // Elevation values were derived from this Figma file.
            // https://www.figma.com/file/eZYR3dtWdb9U90QvJ7p3T9/HPE-Color-Styles?node-id=405%3A25
            // Naming in Figma file is strong/default/weak vs. Grommet t-shirt sizing.
            // As defined here, default is currently mapping to medium.
            light: {
                small: '0px 2px 4px #00000007;',
                medium: '0px 6px 12px #00000007;',
                large: '0px 0px 24px #00000022;',
            },
            dark: {
                small: '0px 2px 4px #0000003D;',
                medium: '0px 6px 12px #0000005C;',
                large: '0px 12px 24px #0000007A;',
            },
        },
    },
    layer: {
        background: {
            light: 'light-1',
            dark: 'dark-1'
        },
        border: {
            intelligentRounding: true
        }
    },
    scale: 1,
    text: {
        xsmall: {
            size: '14px',
            height: '20px',
            maxWidth: '288px',
        },
        small: {
            size: '16px',
            height: '22px',
            maxWidth: '336px',
        },
        medium: {
            size: '18px',
            height: '24px',
            maxWidth: '384px',
        },
        large: {
            size: '22px',
            height: '26px',
            maxWidth: '432px',
        },
        xlarge: {
            size: '28px',
            height: '32px',
            maxWidth: '576px',
        },
        xxlarge: {
            size: '36px',
            height: '40px',
            maxWidth: '768px',
        },
        extend: ({ monospaced, uppercase, bold }) => {
            let result = '';
            result += (monospaced) ? 'font-family: monospace,monospace;' : '';
            result += (uppercase) ? 'text-transform: uppercase;' : '';
            return result;
        },
    },
    radioButton: {
        // In px: Grommet halves the named "small" on phones, leaving the label 6px from the control
        gap: '12px',
        hover: {
            border: {
                color: 'brand'
            }
        }
    },
    paragraph: {
        font: {
            family: 'Calibre, sans-serif',
            weight: 400
        },
        small: {
            size: '16px',
            height: '22px',
            maxWidth: '336px',
        },
        medium: {
            size: '18px',
            height: '24px',
            maxWidth: '384px',
        },
        large: {
            size: '20px',
            height: '26px',
            maxWidth: '432px',
        },
        xlarge: {
            size: '24px',
            height: '32px',
            maxWidth: '576px',
        },
        xxlarge: {
            size: '32px',
            height: '40px',
            maxWidth: '768px',
        },
    },
    dateInput: {
        icon: {
            size: '16px',
        },
    },
    calendar: {
        heading: {
            level: '3',
        },
        day: {
            selected: {
                background: 'focus',
                color: 'white',
            },
        },
        small: {
            fontSize: '13.6px',
            lineHeight: 1.375,
            daySize: '27.43px',
        },
        medium: {
            fontSize: '18px',
            lineHeight: 1.41,
            daySize: '35px',
        },
        large: {
            fontSize: '18px',
            lineHeight: 1.45,
            daySize: '54.86px',
        },
        extend: ({sizeProp}) => {
            return `
                ${sizeProp === 'medium'? 'width: 250px;' : ''}
            `
        },
    },
    list: {
        item: {
            border: undefined,
        },
    },
    button: {
        // Space between a button's icon and label (Grommet default is small, 12px)
        gap: 'xsmall',
        default: {
            color: 'text',
            border: undefined,
            font: {
                weight: 500,
            },
            cursor: "pointer"
        },
        primary: {
            background: {
                color: 'cta',
                opacity: 1
            },
            border: undefined,
            font: {
                weight: 500,
            },
            color: 'black'
        },
        secondary: {
            border: {
                color: 'border',
                width: '1.5px',
            },
            background: {
                color: 'foreground',
                opacity: 1
            },
            font: {
                weight: 500
            },
            color: 'text'
        },
        option: {
            color: 'text',
            border: {
                radius: '0px',
            },
            font: {
                weight: 500,
            }
        },
        hover: {
            background: {
                color: 'hover'
            },
            primary: {
                background: {
                    color: 'cta-hover',
                    opacity: 1
                },
                color: 'black'
            },
            secondary: {
                background: 'hover-secondary'
            },
        },
        active: {
            background: {
                color: 'active',
            },
            color: 'text',
            secondary: {
                border: {
                    color: 'transparent',
                },
            },
            option: {
                background: {
                    color: 'active-background',
                },
            },
        },
        size: {
            small: {
                border: {
                    radius: '4px',
                },
                pad: {
                    vertical: '4px',
                    horizontal: '8px',
                }
            },
            medium: {
                border: {
                    radius: '4px',
                },
                pad: {
                    vertical: '6px',
                    horizontal: '10px',
                }
            },
            large: {
                border: {
                    radius: '6px',
                },
                pad: {
                    vertical: '8px',
                    horizontal: '18px',
                }
            }
        },
        border: {
            radius: '4px',
        },
        pad: {
            vertical: '4px',
            horizontal: '8px',
        },
        extend: (props) => {
            let additionalStyle = '';
            if (!!props.ctaColor) {
                additionalStyle += `
                    background-color: ${normalizeColor(theme.global.colors[props.ctaColor], props.theme)};
                    &:hover {
                        background-color: ${normalizeColor(theme.global.colors[props?.ctaHoverColor || props.ctaColor], props.theme)}${props?.ctaHoverColor ? '' : '88'};
                    }
                `;
            }
            if (!!props.ctaBorderColor) {
                additionalStyle +=  `
                    border: ${normalizeColor(theme.global.colors[props.ctaBorderColor], props.theme)} solid 1.5px;
                    &:hover {
                        border-color: ${normalizeColor(theme.global.colors[props.ctaBorderColor], props.theme)}88;
                    }
                `;
            }
            // Plain buttons (icon-only, or with custom children) have no
            // radius of their own, so their hoverIndicator background was a
            // sharp rectangle. Round it like table row hovers.
            if (props.plain) {
                additionalStyle += ` border-radius: ${props.theme.global.control.border.radius}; `;
            }
            if (props.noBorderRadius) {
                additionalStyle += ` border-radius: 0 `;
            }
            return additionalStyle;
        },
    },
    dropButton: {
        size: {
            small: {
                border: {
                    radius: '4px',
                },
                pad: {
                    vertical: '4px',
                    horizontal: '12px',
                }
            },
            medium: {
                border: {
                    radius: '4px',
                },
                pad: {
                    vertical: '6px',
                    horizontal: '14px',
                }
            },
            large: {
                border: {
                    radius: '6px',
                },
                pad: {
                    vertical: '8px',
                    horizontal: '18px',
                }
            }
        },
    },
    menu: {
        // Sized to its labels (max-content), so Safari has no fraction of a pixel to draw a
        // sideways scrollbar for, and it only ever scrolls up and down. Capped at the screen,
        // where a label too long for a 320px phone wraps rather than being cut off. At least
        // as wide as the drop, which Grommet stretches to a wide button (the account menu at
        // the foot of the sidebar), so the hover highlight reaches its edge.
        container: { width: { min: '100%' }, style: { width: 'max-content', maxWidth: DROP_MAX_WIDTH, overflowX: 'hidden', overflowY: 'auto' } },
    },
    checkBox: {
        // In px: Grommet halves the named "small" on phones, leaving the label 6px from the control
        gap: '12px',
        border: {
            color: "border",
            width: "2px"
        },
        hover: {
            border: {
                color: "border"
            }
        },
        focus: {
            outline: {
                color: "focus"
            }
        },
        toggle: {
            border: {
                color: 'border'
            },
            extend: ({ checked, theme }) => `
                border: 1px solid ${ theme.global.colors.border[theme.dark ? 'dark' : 'light'] };
              ${checked &&
                `background-color: ${theme.global.colors['status-ok'][theme.dark ? 'dark' : 'light']};`}
            `,
            knob: {
                extend: ({ theme }) => `
                    box-shadow: ${ theme.global.elevation[theme.dark ? 'dark' : 'light'].small };
                    border: 1px solid ${ theme.global.colors['neutral-6'][theme.dark ? 'dark' : 'light'] };
                    background-color: white !important; /* weird grommet issue where background-color is a weird accent color they have */
                    color: white;
              `,
            },
        },
        check: {
            radius: '2px',
            extend: ({ theme, checked, indeterminate }) => `
            box-shadow: none;
            background-color: ${
    checked || indeterminate
        ? theme.global.colors['lemon'][theme.dark ? 'dark' : 'light']
        : theme.global.colors.background[theme.dark ? 'dark' : 'light']
};`,
        },
        icon: {
            extend: ({ theme }) => `stroke-width: 2px;
            stroke: ${ theme.global.colors['neutral'][theme.dark ? 'dark' : 'light'] }`,
        },
        extend: () => `
            font-weight: 400;
            width: auto;
        `,
    },
    // One corner for every placeholder. Matches SkeletonBlock in components/charts/ChartSkeleton.js
    skeleton: {
        extend: 'border-radius: 4px;',
    },
    tip: {
        // Plain, so the drop border in global.drop.extend skips tooltips: the content
        // below draws their border
        drop: { plain: true },
        content: {
            round: "xsmall",
            background: {
                color: "foreground",
                opacity: "0.95"
            },
            color: "text",
            elevation: 'none',
            border: { color: "border" }
        }
    },
    anchor: {
        color: 'text',
        textDecoration: 'underline',
        fontWeight: 400,
        hover: {
            textDecoration: 'underline'
        },
    },
    heading: {
        font: {
            family: 'Calibre, sans-serif'
        },
        level: {
            "1": {
                font: { weight: 500 },
                small: {
                    size: "18px",
                    height: "20px"
                },
                medium: {
                    size: "24px",
                    height: "26px"
                },
                large: {
                    size: "32px",
                    height: "34px"
                },
                xlarge: {
                    size: "38px",
                    height: "40px"
                },
            },
            "2": {
                font: { weight: 500 },
                small: {
                    size: "16px",
                    height: "20px"
                },
                medium: {
                    size: "24px",
                    height: "26px"
                },
                large: {
                    size: "28px",
                    height: "28px"
                },
                xlarge: {
                    size: "36px",
                    height: "36px"
                },
            },
        }
    },
    notification: {
        container: {
            pad: {
                vertical: 'small'
            },
            border: {
                color: 'border'
            }
        },
        critical: {
            icon: () => (
                <StatusIcon type="error" size="medium" />
            )
        },
        normal: {
            icon: () => (
                <StatusIcon type="ok" size="medium" />
            )
        },
        close: {
            icon: () => (
                <Box pad="xsmall">
                    <Icon
                        SelectedIcon={IconClose}
                        size="xxxsmall"
                    />
                </Box>
            )
        },
        title: {
            weight: "normal"
        }
    },
    accordion: {
        panel: {
            border: {
                color: 'border',
                side: 'bottom',
            },
        },
        heading: {
            margin: { vertical: 'medium', horizontal: 'xsmall' },
        },
        hover: {
            // Same hover as other list rows (e.g. payment methods): the panel
            // header is a plain button, so it also gets the plain-button radius
            background: 'hover-subtle',
            heading: {
                color: undefined,
            },
        },
        border: undefined,
        icons: {
            color: 'text',
        },
    },
    select: {
        clear: {
            container: {
                background: 'transparent',
                pad: 'xsmall',
                hover: {
                    background: 'hover',
                },
            }
        },
        control: {
            extend: ({ disabled }) => `
                ${disabled &&
                `
                opacity: 0.3;
                input {
                    cursor: default;
                }`}
            `,
        },
        icons: {
            color: 'text',
        },
        options: {
            container: {
                pad: 'xsmall',
            },
            text: {
                weight: 400,
            },
        },
        // The drop it sits in draws the border (global.drop.extend), so only clip the options to its corners
        container: {
            extend: () => `
                overflow: hidden;

                button:first-child {
                    border-top-left-radius: ${DROP_INNER_RADIUS};
                    border-top-right-radius: ${DROP_INNER_RADIUS};
                }
                button:last-child {
                    border-bottom-left-radius: ${DROP_INNER_RADIUS};
                    border-bottom-right-radius: ${DROP_INNER_RADIUS};
                }
            `,
        },
    },
    fileInput: {
        round: "xsmall",
        pad: "xsmall"
    },
    textInput: {
        // The drop it sits in draws the border (global.drop.extend), so only clip the suggestions to its corners
        suggestions: {
            extend: () => `
                overflow: hidden;

                li:first-child button {
                    border-top-left-radius: ${DROP_INNER_RADIUS};
                    border-top-right-radius: ${DROP_INNER_RADIUS};
                }
                li:last-child button {
                    border-bottom-left-radius: ${DROP_INNER_RADIUS};
                    border-bottom-right-radius: ${DROP_INNER_RADIUS};
                }
            `,
        },
    },
    formField: {
        focus: {
            border: {
                color: 'focus'
            }
        },
        border: {
            color: "border-strong",
            error: {
                color: {
                    dark: 'white',
                    light: 'status-critical',
                },
            },
            position: 'inner',
            side: 'all',
            size: 'small',
            style: 'solid',
        },
        content: {
            margin: { vertical: 'xsmall' },
            pad: 'small',
        },
        disabled: {
            background: {
                color: 'status-disabled',
                opacity: 'weak',
            },
            border: {
                color: 'border-weak',
            },
            label: {
                color: 'text-weak',
            },
        },
        error: {
            background: {
                color: 'validation-critical',
            },
            container: {
                gap: 'xsmall',
            },
            color: 'status-critical',
            margin: {
                bottom: 'xsmall',
                top: 'none',
                horizontal: 'none',
                start: 'xxsmall',
            },
            size: 'small',
            icon: false
        },
        help: {
            color: 'dark-3',
            margin: {
                start: '0',
            },
            size: 'small',
            icon: false
        },
        info: {
            size: 'xsmall',
            color: 'text-xweak',
            margin: {
                bottom: 'xsmall',
                top: 'none',
                horizontal: 'none',
                start: 'xxsmall',
            },
        },
        label: {
            requiredIndicator: true,
            margin: {
                top: 'xsmall',
                bottom: 'none',
                horizontal: 'none',
            },
            weight: 500,
            color: 'text-strong',
            size: 'small',
        },
        margin: 'none',
        round: '4px',
        extend: (props) => {
            let additionalStyle = '';

            // Disable hover effects on div and span elements inside labels
            additionalStyle += `
                label div:hover,
                label span:hover {
                    background-color: transparent !important;
                }
            `;

            if (props.className === 'small') {
                additionalStyle += `
                    input {
                        padding: 5px 10px;
                    }
                `;
            }

            return additionalStyle;
        },
    }
};

/*
    Reads the theme cookie written by ThemeProvider so the server can render the same mode
    the browser is about to paint. Without this the server always rendered light while the
    client hydrated from the cookie, so a dark-mode user got light markup and light
    styled-components output underneath a dark hydration pass.

    The cookie holds the RESOLVED mode rather than the preference, because 'system' depends
    on prefers-color-scheme and the server cannot see it. 'system' is still normalised here
    for cookies written by older builds, where light is the safe default.
*/
export const readThemeCookie = (cookieHeader) => {
    const match = /(?:^|;\s*)theme=([^;]*)/.exec(cookieHeader || '');
    return match && match[1] === 'dark' ? 'dark' : 'light';
};

/*
    The blocking script pages/_document.js puts in <head>. It hands the browser's first
    render the mode the server actually rendered, which is not always the cookie's.

    Pages Next optimises statically are rendered once at build time, with no request and so
    no cookie, and their HTML is always light. Re-reading the cookie in the browser gave a
    dark-mode user a dark first render over that light HTML. React 18 does not patch
    attributes that differ during hydration, so the Grommet wrapper in _app.js kept its
    light class for the rest of the visit while every page after it rendered dark.

    Baking in the server's mode keeps hydration exact. On a static page the dark user
    hydrates light and next-themes switches to dark after mount, in a normal re-render that
    React does apply.
*/
export const themeBootstrapScript = (serverTheme) =>
    `window.__INITIAL_THEME__ = '${serverTheme === 'dark' ? 'dark' : 'light'}';`;

/*
    Tells the browser's first render which user agent this HTML was rendered for, the same
    way __INITIAL_THEME__ carries the mode.

    Grommet picks its breakpoint from a userAgent prop, and without one falls back to tablet
    - so a phone was served a tablet layout and only re-laid itself out once the resize
    effect ran after mount, moving every block on the page. Reading navigator here instead
    would reintroduce that as a hydration mismatch: this HTML can be served to a device that
    did not request it, either because the page was optimised at build time or because /p/
    is cached at the edge. The browser has to render what the server rendered, and the
    resize effect corrects the layout a moment later either way.

    JSON.stringify to quote and escape it: a user agent is attacker-controlled input being
    written into a script tag, and </script> or a quote in it would otherwise break out.
*/
export const userAgentBootstrapScript = (userAgent) =>
    `window.__USER_AGENT__ = ${JSON.stringify(userAgent || '').replace(/</g, '\\u003c')};`;

export default theme;
